import { AnimatedImageDto, AnimatedImageType } from "@repo/api/animated-image";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { ImageListItemStatus, useDuration } from "./image-list-item-hooks";
import { TimelineItemToAnimate } from "@/lib/stores/editor-store/editor-state";
import {
  IconArrowsLeftRight,
  IconCrop,
  IconExclamationCircle,
  IconImageGeneration,
} from "@tabler/icons-react";

import { getAnimatedImageConfig } from "@repo/api/utils/composition";
import { Button } from "@/lib/components/Button/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import FormRow from "./FormRow";
import SlowMotionPopover from "./SlowMotionPopover";
import VolumePopover from "./VolumePopover";
import SliderInputField from "@/lib/components/SliderInputField";
import { Slider } from "@/lib/components/Slider";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import { CreativeProjectTimelineItem } from "@repo/api/creative-project";
import QuickAnimationsDialog from "./QuickAnimations/QuickAnimationsDialog";
import Image from "@/lib/components/Image";
import { SourceImageDto } from "@repo/api/source-image";

type FormControlsProps = {
  timelineItem: CreativeProjectTimelineItem;
  animatedImage?: AnimatedImageDto;
  timelineItemFormData?: TimelineItemToAnimate;
  status: ImageListItemStatus;
};

export default function ImageFormControls({
  timelineItem,
  animatedImage,
  timelineItemFormData,
  status,
}: FormControlsProps) {
  if (status === "PROCESSING") {
    return <AnimationLoader type={animatedImage?.type ?? "GENERATED"} />;
  }

  if (status === "FAILED") {
    return <FailedIndicator type={animatedImage?.type ?? "GENERATED"} />;
  }

  if (status === "SELECTED") {
    return (
      <ImageSelectedForm
        key={timelineItem.externalId}
        timelineItemExternalId={timelineItem.externalId}
        timelineItemFormData={timelineItemFormData!}
      />
    );
  }

  if (status === "COMPLETED") {
    return (
      <ImageCompletedForm
        key={timelineItem.externalId + timelineItem.animatedImageExternalId}
        timelineItem={timelineItem}
        animatedImage={animatedImage!}
      />
    );
  }

  return null;
}

type CompletedFormProps = {
  timelineItem: CreativeProjectTimelineItem;
  animatedImage: AnimatedImageDto;
};

function ImageCompletedForm({
  timelineItem,
  animatedImage,
}: CompletedFormProps) {
  const updateVideo = useEditorStore((state) => state.service.updateVideo);
  const cropperState = useEditorStore((state) => state.cropperState);
  const setCropperState = useEditorStore(
    (state) => state.service.setCropperState,
  );

  const {
    value: durationValue,
    minValue: durationMinValue,
    maxValue: durationMaxValue,
    handleChange: handleDurationChange,
    handleCommit: handleDurationCommit,
  } = useDuration(timelineItem, animatedImage);

  const config = getAnimatedImageConfig(timelineItem);

  const isCropping =
    cropperState?.timelineItemExternalId === timelineItem.externalId &&
    cropperState?.animatedImageExternalId ===
      timelineItem.animatedImageExternalId;

  return (
    <div className="flex h-full w-full flex-col justify-center gap-1 pl-5">
      <FormRow label="Duration (s)">
        <Slider
          value={durationValue}
          onValueChange={handleDurationChange}
          onValueCommit={handleDurationCommit}
          min={durationMinValue}
          max={durationMaxValue}
          step={0.1}
        />
        <SliderInputField
          value={durationValue}
          onCommit={(newValue) => handleDurationCommit(newValue)}
          onCommitMultiple={(newValue) =>
            handleDurationCommit(newValue, "COMMIT_MULTIPLE")
          }
          min={durationMinValue}
          max={durationMaxValue}
          inputDecimalPlaces={3}
        />
      </FormRow>

      <FormRow label="">
        {animatedImage.backwardSrc && (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="iconXs"
                  variant={config.reverse ? "default" : "outline"}
                  onClick={() => {
                    updateVideo((video) => {
                      const timelineItemToEdit =
                        video.compositionJson.mainSceneTimeline.find(
                          (item) => item.externalId === timelineItem.externalId,
                        )!;

                      const configToEdit =
                        getAnimatedImageConfig(timelineItemToEdit);
                      configToEdit.reverse = !configToEdit.reverse;
                    });
                  }}
                >
                  <IconArrowsLeftRight />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Reverse</p>
              </TooltipContent>
            </Tooltip>

            <div className="w-2"></div>
          </>
        )}

        <SlowMotionPopover
          timelineItem={timelineItem}
          animatedImage={animatedImage}
        />

        <div className="w-2"></div>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="iconXs"
              variant={isCropping ? "default" : "outline"}
              className={`[&_svg]:size-5 ${animatedImage.height ? "" : "hidden"}`}
              onClick={() => {
                if (!timelineItem.animatedImageExternalId) {
                  throw new Error("Timeline item has no animated image");
                }

                const value = isCropping
                  ? null
                  : {
                      timelineItemExternalId: timelineItem.externalId,
                      animatedImageExternalId:
                        timelineItem.animatedImageExternalId,
                    };

                setCropperState(value);
              }}
            >
              <IconCrop />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Crop</p>
          </TooltipContent>
        </Tooltip>

        {animatedImage.hasAudio && (
          <>
            <div className="w-2"></div>
            <VolumePopover timelineItem={timelineItem} />
          </>
        )}
      </FormRow>
    </div>
  );
}

type SelectedFormProps = {
  timelineItemExternalId: string;
  timelineItemFormData: TimelineItemToAnimate;
};

function ImageSelectedForm({
  timelineItemExternalId,
  timelineItemFormData,
}: SelectedFormProps) {
  const updateAnimationForm = useEditorStore(
    (state) => state.service.updateAnimationForm,
  );
  const setSourceImage = useEditorStore(
    (state) => state.service.setSourceImage,
  );

  const endSourceImage = useEditorStore((state) =>
    state.service.getSourceImageByExternalId(
      timelineItemFormData.endImageExternalId ?? undefined,
    ),
  );

  const startSourceImage = useEditorStore((state) => {
    const timelineItem = state.video?.compositionJson.mainSceneTimeline.find(
      (item) => item.externalId === timelineItemExternalId,
    );
    return state.service.getSourceImageByExternalId(
      timelineItem?.startSourceImageExternalId ?? undefined,
    );
  });

  const updateVideo = useEditorStore((state) => state.service.updateVideo);

  const handlePromptTypeChange = (
    promptType: string,
    startImage: SourceImageDto | null,
    endImage: SourceImageDto | null,
  ) => {
    if (endImage) {
      setSourceImage(endImage);
    }

    if (startImage) {
      setSourceImage(startImage);
      updateVideo((video) => {
        const item = video.compositionJson.mainSceneTimeline.find(
          (timelineItem) => timelineItem.externalId === timelineItemExternalId,
        );
        if (item) {
          item.startSourceImageExternalId = startImage.externalId;
        }
      });
    }

    updateAnimationForm((currentAnimationForm) => {
      const item = currentAnimationForm.items[timelineItemExternalId]!;
      item.promptType = promptType;
      item.endImageExternalId = endImage?.externalId ?? null;
    });
  };

  const openCreativeProjectSheet = useEditorStore(
    (state) => state.service.openCreativeProjectSheet,
  );

  return (
    <div className="flex h-full w-full flex-col justify-center pl-5">
      <span className="mb-1.5 ml-1.5 text-sm font-medium">Animation</span>
      <div className="flex flex-row items-center gap-3">
        <QuickAnimationsDialog
          timelineItemExternalId={timelineItemExternalId}
          onAnimationSelected={handlePromptTypeChange}
          currentPromptType={timelineItemFormData.promptType}
          startImage={startSourceImage ?? null}
          endImage={endSourceImage ?? null}
        />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="iconSm"
              variant="outline"
              className="shrink-0 basis-auto rounded-md"
              onClick={() => openCreativeProjectSheet(timelineItemExternalId)}
            >
              <IconImageGeneration />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Creative Mode</p>
          </TooltipContent>
        </Tooltip>
      </div>
      {endSourceImage && (
        <div className="mt-2 h-10.5 w-16 overflow-hidden rounded-sm border border-grey-200">
          <Tooltip>
            <TooltipTrigger className="block h-full w-full">
              <Image
                src={endSourceImage.src}
                className="h-full w-full object-cover"
                alt="End source image"
              />
            </TooltipTrigger>
            <TooltipContent>
              <p>This is image is used as the end image for the animation.</p>
            </TooltipContent>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

function AnimationLoader({ type }: { type: AnimatedImageType }) {
  const title = type === "GENERATED" ? "Animating image" : "Processing video";
  const subtitle =
    type === "GENERATED"
      ? `This takes about 3 minutes`
      : "Preparing a preview version";

  return (
    <div className="flex h-full w-full flex-col items-center justify-center pl-5">
      <LoadingSpinner size={20} />
      <div className="h-1.5"></div>
      <p className="text-sm font-semibold">{title}</p>
      <p className="text-sm text-text-secondary">{subtitle}</p>
    </div>
  );
}

function FailedIndicator({ type }: { type: AnimatedImageType }) {
  const title =
    type === "GENERATED"
      ? "Failed to animate image"
      : "Failed to process video";
  const subtitle =
    type === "GENERATED" ? (
      <span>
        Credits have been refunded.
        <br />
        Please try again or contact support.
      </span>
    ) : (
      "Please try again or contact support."
    );

  return (
    <div className="flex h-full w-full flex-col items-center justify-center pl-5 text-center">
      <IconExclamationCircle size={20} className="text-error" />
      <div className="h-1.5"></div>
      <p className="text-sm font-semibold">{title}</p>
      <p className="text-sm text-text-secondary">{subtitle}</p>
    </div>
  );
}
